import React from 'react';
import { BsFillFileEarmarkTextFill as FileTextIcon } from 'react-icons/bs';
import { AiFillFlag as FlagIcon } from 'react-icons/ai';
import { MdAdd as AddIcon } from 'react-icons/md';
import { EmptyCard } from 'components/emptyCard/emptyCard';

import { FaEllipsisH as EllipsisIcon } from 'react-icons/fa';

import classNames from 'classnames';
import { size } from 'lodash';
import ReactTooltip from 'rc-tooltip';

import { Tooltip } from 'components/tooltip/tooltip';
import { ProjectsDropdown } from 'components/projectsDropdown/projectsDropdown';
import { Avatar, avatarOptions } from 'components/avatar/avatar';
import { Heading } from 'components/heading/heading';
import { Button } from 'components/button/button';

import styles from './applicantCard.module.scss';
import { OverlayContainer } from '@containers/overlayContainer/OverlayContainer';
import { views } from '@constants/views';
import { useSelector } from 'react-redux';
import { useState, useRef, useEffect } from 'react';
import { calculateProfileCompletion } from '../../utils/profileCompletion';

// Utility function to get background color based on profile completion percentage
const getCompletionColor = (percentage) => {
  if (percentage >= 60) return '#DEF1C6'; // Green for 60-100%
  if (percentage >= 20) return '#FFE9BF'; // Yellow for 20-60%
  return '#FECDCC'; // Red for 0-20%
};

// Utility function to get text color based on profile completion percentage
const getCompletionTextColor = (percentage) => {
  if (percentage >= 60) return '#7FB539'; // Dark green text for 60-100%
  if (percentage >= 20) return '#F5A623'; // Orange text for 20-60%
  return '#E85D75'; // Dark red text for 0-20%
};

export const ApplicantCard = (props) => {
  const {
    applicant,
    openOverlay,
    coverLetter,
    projects,
    job,
    clientProjectsOptions,
    create,
    openTalentSidebar,
    fullName,
    currentJob,
    locationInfo,
    openapplicantSidebar,
    emptyMessage,
    coverLetterOpen,
    handleCoverLetterOpening,
    key,
  } = props;

  const avatarSize = 20;
  const currentUIState = useSelector((state) => state.commonReducer.viewsData);
  const flagContainerRef = useRef(null);

  const [isCoverLetter, setIsCoverLetter] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(null);

  // Calculate profile completion percentage when applicant data changes
  useEffect(() => {
    if (applicant) {
      const completion = calculateProfileCompletion(applicant);
      setProfileCompletion(completion);
    }
  }, [applicant, applicant?.experiences, applicant?.educations, applicant?.achievements, applicant?.user_languages_proficiencies]);

  // Clean up any stray text nodes that might contain "0"
  useEffect(() => {
    if (flagContainerRef.current) {
      const container = flagContainerRef.current;
      // Remove any direct text nodes that contain only numbers
      Array.from(container.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && /^\s*\d+\s*$/.test(node.textContent)) {
          node.remove();
        }
      });
    }
  });

  return (
    <div
      className={classNames(styles.col_1_3, ' col-6', styles.maxWidth240, {
        ['col-lg-4']: currentUIState?.currentView,
        ['col-lg-3']: !currentUIState?.currentView,
      })}
      key={key}
    >
      <div
        className={classNames(
          styles.compCardContainer,
          styles.smallMarginsGrid,
          styles.card,
          styles.applicantCard,
        )}
      >
        {emptyMessage && (
          <EmptyCard
            title="There were no results for your search"
            description="Tweak your search criteria for better results."
          />
        )}
        {!emptyMessage && (
          <>
            {/* Profile Completion Indicator - positioned at card level */}
            {profileCompletion !== undefined && profileCompletion !== null && (
              <div 
                className={classNames(styles.profileCompletionBadge)}
                style={{ 
                  backgroundColor: getCompletionColor(profileCompletion),
                  color: getCompletionTextColor(profileCompletion)
                }}
              >
                {profileCompletion}%
              </div>
            )}
            
            <div ref={flagContainerRef} className={classNames(styles.col_1_1, styles.flagLinkContainer)}>
              {/*<If condition={ size(talent.cards) >= 1 } >*/}
              {applicant?.notes?.length && (
                <ReactTooltip
                  placement="top"
                  overlay={
                    <Tooltip
                      title="Flagged candidate"
                      content="Candidate has either been added to a pipeline
                                      before, or a note or attachment has been left on candidate."
                    />
                  }
                >
                  <span className={classNames(styles.alert, styles.small, styles.green)}>
                    <FlagIcon size={14} />
                  </span>
                </ReactTooltip>
              )}
              {coverLetterOpen && isCoverLetter && (
                <OverlayContainer
                  closeOverlay={() => {
                    handleCoverLetterOpening();
                    setIsCoverLetter(false);
                  }}
                  currentView={coverLetterOpen && views.coverLetter}
                  talentCoverLetter={coverLetter}
                  talentName={applicant?.full_name}
                  talentJobTitle={applicant.secondHeading}
                  talentAvatar={applicant.avatar}
                />
              )}
              {coverLetter && (
                <a
                  className={classNames(
                    styles.alert,
                    styles.small,
                    styles.blue,
                    styles.cursor_pointer,
                  )}
                >
                  <FileTextIcon
                    size={13}
                    onClick={() => {
                      handleCoverLetterOpening();
                      setIsCoverLetter(true);
                    }}
                  />
                </a>
              )}
              {/* Project count badge removed to eliminate "0" display */}
            </div>

            <div className={classNames(styles.compCardContent)}>
              <center>
                <div className={classNames(styles.avatarLink)} onClick={openTalentSidebar}>
                  <Avatar
                    avatar={{ ...avatarOptions(applicant, 'talent') }}
                    name={applicant?.full_name}
                    src={applicant.avatar}
                    className={classNames(styles.avatar, styles.img_avatar)}
                    size={avatarSize.large}
                  />
                  <Heading size="medium" className={classNames(styles.firstHeading, 'mt-3')}>
                    {applicant?.full_name}
                  </Heading>
                </div>

                <p className={styles.secondHeading} style={{ padding: '0px' }}>
                  {applicant.current_experience
                    ? applicant?.current_experience.title +
                      ', ' +
                      applicant?.current_experience.company_name +
                      ', ' +
                      applicant?.current_experience.area
                    : ''}
                </p>

                <div className="d-flex justify-content-center">
                  <ProjectsDropdown
                    projectsData={projects}
                    applicant={applicant}
                    // onClick={(project, column) => {
                    //   create('cards', {
                    //     talent: talent.url,
                    //     pipeline: project.pipeline.url,
                    //     column,
                    //     before: null,
                    //   });
                    // }}
                    zIndex={12}
                    inline
                    showClientColumn={true} 
                    clientProjectStageFlow={true}
                  >
                    <Button inline square submit size={'small'}>
                      <AddIcon size={15} style={{ verticalAlign: 'center' }} />
                      {/* </center> */}
                    </Button>
                  </ProjectsDropdown>
                  <Button
                    // onClick={openTalentSidebar}
                    inline
                    square
                    white
                    size={'small'}
                  >
                    <EllipsisIcon size={15} />
                  </Button>
                </div>
              </center>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
